/**
 * Canvas Floor Viewer
 * Canvas-based floor renderer for Viewer mode
 * Replaces JointJS SVG rendering with high-performance Canvas
 */

import { useRef, useEffect } from 'react'
import type { dia } from '@joint/core'
import { Floor } from '../../../../map-editor-shared/store'
import { CanvasRenderer } from '../../canvas'
import type { TemplateRelationType } from '../../../../map-editor-entities/schema/templateSchema'
import { useStatusStore } from '../../../status/model/statusStore'
import { useCanvasPanning } from './useCanvasPanning'
import { useCanvasZoom } from './useCanvasZoom'
import { log, LogCategory } from '../../../../map-editor-shared/lib/logger'
import styles from './CanvasFloorViewer.module.css'

export interface CanvasFloorViewerProps {
  floor: Floor
  onZoomChange?: (zoom: number) => void
  externalZoom?: number
  synchronizedZoom?: boolean
  /**
   * Live editable graph. When provided, the renderer binds to it (re-rendering
   * on cell add/remove/change) instead of loading the floor's static graphJson —
   * this is the viewer-mode editing path where features mutate the graph model.
   */
  liveGraph?: dia.Graph | null
  /** Template relation config, enabling the relationship overlay on selection. */
  relationTypes?: Record<string, TemplateRelationType>
  /** Currently selected cell ids (highlighted). */
  selectedIds?: ReadonlySet<string>
  /** Fired on a canvas click: the picked cell id, or null for empty space. */
  onSelectElement?: (id: string | null) => void
  /**
   * 셀을 클릭했을 때(빈 공간 제외) id·식별 이름·CCTV/주차면 여부를 전달한다.
   * 시뮬레이터 비상벨 매칭·CCTV 선택(isCctv)·주차면 점유 토글(isParking)에 사용.
   */
  onMapClick?: (
    cellId: string,
    cellName: string | null,
    isCctv: boolean,
    isParking: boolean,
  ) => void
  /** 점유로 빨갛게 칠할 주차면 cell id 목록(만공차 체크 탭). */
  occupiedParkingIds?: string[]
  /**
   * 포커스 지점(world 좌표) — 시뮬레이터에서 선택된 객체 위치에 펄스 링을 그리고
   * nonce 가 바뀔 때마다 그 지점으로 뷰포트를 센터링한다. null 이면 링 해제.
   */
  focusPoint?: { x: number; y: number; nonce: number } | null
  /** CCTV 이름 라벨 표시(기본 true). */
  showCctvNames?: boolean
  /** 주차면 이름 라벨 표시(기본 false). */
  showParkingNames?: boolean
  /** CCTV↔주차면 연결선 표시(기본 true). */
  showCctvLinks?: boolean
}

export function CanvasFloorViewer({
  floor,
  onZoomChange,
  externalZoom,
  synchronizedZoom = false,
  liveGraph,
  relationTypes,
  selectedIds,
  onSelectElement,
  onMapClick,
  occupiedParkingIds,
  focusPoint,
  showCctvNames = true,
  showParkingNames = false,
  showCctvLinks = true,
}: CanvasFloorViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const rendererRef = useRef<CanvasRenderer | null>(null)
  const onSelectRef = useRef(onSelectElement)
  onSelectRef.current = onSelectElement
  const onMapClickRef = useRef(onMapClick)
  onMapClickRef.current = onMapClick

  // 실시간 상태(주차 점유·CCTV 끊김·조명). StatusOverlay 가 읽던 것과 동일한 스토어.
  const parkingStatuses = useStatusStore(s => s.parkingStatuses)
  const cctvStatuses = useStatusStore(s => s.cctvStatuses)
  const lightingStatuses = useStatusStore(s => s.lightingStatuses)
  const lightingBaseline = useStatusStore(s => s.lightingBaseline)
  const emergencyCctvs = useStatusStore(s => s.emergencyCctvs)
  const emergencyLightIdsByCctv = useStatusStore(s => s.emergencyLightIdsByCctv)

  // Initialize Canvas Renderer
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    log.info(LogCategory.CANVAS, `[${floor.name}] Initializing Canvas renderer...`)

    try {
      const renderer = new CanvasRenderer(canvas, {
        enableCulling: true,
        debug: false,
      })
      rendererRef.current = renderer

      log.info(LogCategory.CANVAS, `[${floor.name}] Renderer initialized`)
    } catch (error) {
      log.error(LogCategory.CANVAS, `[${floor.name}] Failed to initialize renderer`, error)
    }

    return () => {
      if (rendererRef.current) {
        rendererRef.current.destroy()
        rendererRef.current = null
        log.debug(LogCategory.CANVAS, `[${floor.name}] Renderer destroyed`)
      }
    }
  }, [floor.name])

  // Load graph data: bind to the live graph when provided (viewer-mode editing),
  // otherwise render the floor's static saved graphJson (read-only preview).
  useEffect(() => {
    const renderer = rendererRef.current
    if (!renderer) return

    try {
      if (liveGraph) {
        renderer.bindToGraph(liveGraph)
        log.info(LogCategory.CANVAS, `[${floor.name}] Bound to live graph`)
      } else if (floor.mapData?.graphJson) {
        renderer.loadFromGraphJson(floor.mapData.graphJson)
        log.info(LogCategory.CANVAS, `[${floor.name}] Graph loaded and rendered`)
      } else {
        log.debug(LogCategory.CANVAS, `[${floor.name}] No live graph or graphJson to load`)
      }
    } catch (error) {
      log.error(LogCategory.CANVAS, `[${floor.name}] Failed to load graph`, error)
    }

    return () => {
      if (liveGraph) renderer.unbindFromGraph()
    }
  }, [liveGraph, floor.id, floor.mapData?.graphJson, floor.name])

  // Push the relation config so the overlay can draw on selection.
  useEffect(() => {
    rendererRef.current?.setRelationTypes(relationTypes)
  }, [relationTypes])

  // Mirror the external selection into the renderer highlight.
  useEffect(() => {
    rendererRef.current?.setSelection(selectedIds ?? [])
  }, [selectedIds])

  // 실시간 상태를 렌더러에 주입(주차 점유 색·CCTV 끊김·조명 bloom). floor.id 를
  // 의존성에 둬 층 전환으로 렌더러가 재생성돼도 다시 밀어 넣는다. 스토어가 비어
  // 있으면(피드 미연결) 아무 효과 없음 → 안전.
  useEffect(() => {
    rendererRef.current?.setStatus({
      floorId: floor.id,
      parkingStatuses,
      cctvStatuses,
      lightingStatuses,
      lightingBaseline,
      emergencyCctvs,
      emergencyLightIds: Object.values(emergencyLightIdsByCctv).flat(),
    })
  }, [floor.id, parkingStatuses, cctvStatuses, lightingStatuses, lightingBaseline, emergencyCctvs, emergencyLightIdsByCctv])

  // 만공차 체크: 점유된 주차면을 빨갛게.
  useEffect(() => {
    rendererRef.current?.setOccupiedParking(occupiedParkingIds ?? [])
  }, [occupiedParkingIds])

  // 포커스 링 + 센터링 — nonce 가 바뀔 때마다(같은 객체 재선택 포함) 다시 센터링.
  useEffect(() => {
    const renderer = rendererRef.current
    if (!renderer) return
    if (!focusPoint) {
      renderer.setFocusPoint(null)
      return
    }
    renderer.setFocusPoint({ x: focusPoint.x, y: focusPoint.y })
    renderer.centerOn(focusPoint.x, focusPoint.y)
  }, [focusPoint])

  // 라벨(CCTV/주차면 이름)·연결선 표시 토글.
  useEffect(() => {
    rendererRef.current?.setLabelVisibility({
      cctv: showCctvNames,
      parking: showParkingNames,
      links: showCctvLinks,
    })
  }, [showCctvNames, showParkingNames, showCctvLinks])

  // Click-to-select: a pointerup with negligible movement (i.e. not a pan drag)
  // hit-tests the canvas and reports the picked cell id. Handlers read live refs
  // so they survive renderer re-creation without re-binding.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const CLICK_MOVE_THRESHOLD = 3
    let downX = 0
    let downY = 0
    let moved = false

    const onPointerDown = (e: PointerEvent) => {
      downX = e.clientX
      downY = e.clientY
      moved = false
    }
    const onPointerMove = (e: PointerEvent) => {
      if (Math.abs(e.clientX - downX) > CLICK_MOVE_THRESHOLD || Math.abs(e.clientY - downY) > CLICK_MOVE_THRESHOLD) {
        moved = true
      }
    }
    const onPointerUp = (e: PointerEvent) => {
      if (moved) return
      const renderer = rendererRef.current
      if (!renderer) return
      const rect = canvas.getBoundingClientRect()
      // CCTV>주차면>최상위 우선 픽킹(겹친 조명선/구역에 가려지지 않게).
      const pick = renderer.pickInteractive(e.clientX - rect.left, e.clientY - rect.top)
      if (pick && onMapClickRef.current) {
        onMapClickRef.current(pick.id, pick.name, pick.isCctv, pick.isParking)
      }
      onSelectRef.current?.(pick?.id ?? null)
    }

    canvas.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
    }
  }, [])

  // Handle canvas resize
  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    const renderer = rendererRef.current

    if (!canvas || !container || !renderer) return

    const updateSize = () => {
      const width = container.clientWidth
      const height = container.clientHeight

      if (width > 0 && height > 0) {
        canvas.width = width
        canvas.height = height
        // renderer.resize 가 실제 크기를 처음 얻은 시점에 1회 fitToContent 한다
        // (인스턴스별 플래그라 StrictMode 이중 마운트에도 안전).
        renderer.resize(width, height)
        log.debug(LogCategory.CANVAS, `[${floor.name}] Resized to ${width}x${height}`)
      }
    }

    // Initial size — setTimeout 은 cleanup 에서 취소(stale 렌더러 참조/StrictMode 누수 방지).
    const initialTimer = window.setTimeout(updateSize, 100)

    const resizeObserver = new ResizeObserver(updateSize)
    resizeObserver.observe(container)

    return () => {
      window.clearTimeout(initialTimer)
      resizeObserver.disconnect()
    }
  }, [floor.name])

  // Canvas panning (mouse drag)
  useCanvasPanning(canvasRef, (dx, dy) => {
    if (rendererRef.current) {
      rendererRef.current.pan(dx, dy)
    }
  })

  // Canvas zooming (mouse wheel). useCanvasZoom 은 줌 "배수"(0.9/1.1)를 넘기므로
  // 현재 스케일에 곱해 절대 목표 스케일로 변환한다(이전엔 배수가 절대 스케일로
  // 잘못 전달돼 휠 한 번에 0.9/1.1 로 고정됐다). 범위는 JointJS 경로와 동일 [0.02, 4].
  useCanvasZoom(canvasRef, (factor, centerX, centerY) => {
    const renderer = rendererRef.current
    if (!renderer) return
    const next = Math.max(0.02, Math.min(renderer.getScale() * factor, 4))
    renderer.zoom(next, centerX, centerY)
    if (onZoomChange) {
      onZoomChange(next)
    }
  })

  // Handle external zoom changes (synchronized zoom)
  useEffect(() => {
    if (!synchronizedZoom || externalZoom === undefined) return
    if (!rendererRef.current) return

    log.debug(LogCategory.CANVAS, `[${floor.name}] Applying external zoom: ${externalZoom}`)
    rendererRef.current.zoom(externalZoom)
  }, [externalZoom, synchronizedZoom, floor.name])

  return (
    <div ref={containerRef} className={styles.container}>
      <canvas ref={canvasRef} className={styles.canvas} />
      <div className={styles.floorBadge}>{floor.name}</div>
    </div>
  )
}
